import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { findExhibitedArtist, getExhibitedArtists } from '../lib/data';
import { useLanguage } from '../lib/LanguageContext';

const ArtistDetailPage = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const ro = language === 'ro';
  const artist = findExhibitedArtist(id, language);

  if (!artist) return <Navigate to="/artists" replace />;

  const artists = getExhibitedArtists(language);
  const index = artists.findIndex((item) => item.id === artist.id);
  const previous = index > 0 ? artists[index - 1] : null;
  const next = index < artists.length - 1 ? artists[index + 1] : null;

  return (
    <div className="bg-bone text-charcoal min-h-screen" data-testid="artist-detail-page">
      <Header />

      <main>
        <section className="pt-36 md:pt-44 lg:pt-52 px-6 md:px-12 lg:px-16 pb-14 md:pb-20">
          <div className="grid grid-cols-12 gap-x-6 gap-y-10">
            <Reveal delay={0.08} className="col-span-12">
              <nav className="text-[11px] tracking-eyebrow uppercase text-charcoal/50 mb-6 flex items-center gap-2">
                <Link to="/" className="hover:opacity-60 transition-opacity">
                  {ro ? 'Acasă' : 'Home'}
                </Link>
                <span>/</span>
                <Link to="/artists" className="hover:opacity-60 transition-opacity">
                  {ro ? 'Artiști' : 'Artists'}
                </Link>
                <span>/</span>
                <span className="text-charcoal/80 truncate max-w-[180px] md:max-w-none">
                  {artist.name}
                </span>
              </nav>

              <span className="text-[11px] tracking-eyebrow uppercase text-charcoal/55">
                {ro ? 'Artist expus' : 'Exhibited artist'} · {artist.number}
              </span>

              <h1
                className="mt-6 font-sans font-extrabold text-charcoal leading-[0.9] tracking-normal text-6xl sm:text-7xl md:text-[9vw] lg:text-[8vw]"
                data-testid="artist-detail-title"
              >
                {artist.name}
              </h1>
            </Reveal>
          </div>
        </section>

        <section className="px-6 md:px-12 lg:px-16 pb-20 md:pb-28">
          <div className="grid grid-cols-12 gap-x-6 gap-y-10 border-t border-charcoal pt-8 md:pt-10">
            <Reveal className="col-span-12 lg:col-span-5">
              <div className="relative w-full aspect-[4/5] overflow-hidden bg-beige">
                <motion.img
                  src={artist.image}
                  alt={artist.name}
                  initial={{ scale: 1.06 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 w-full h-full object-contain"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </Reveal>

            <div className="col-span-12 lg:col-span-6 lg:col-start-7">
              <Reveal>
                <h2 className="text-[11px] tracking-eyebrow uppercase text-charcoal/55">
                  {ro ? 'Statement' : 'Statement'}
                </h2>
                <p className="mt-7 text-xl md:text-2xl leading-relaxed text-charcoal/80 font-bold whitespace-pre-line">
                  {artist.statement || (ro ? 'Descrierea artistului va fi adăugată.' : 'Artist description will be added.')}
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-36 border-t border-charcoal pt-16 md:pt-24">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-16">
              <h2 className="font-sans font-extrabold text-4xl md:text-5xl leading-none">
                {ro ? 'Lucrări' : 'Works'}
              </h2>
              <span className="text-[11px] tracking-eyebrow uppercase text-charcoal/50">
                {artist.works.length} {ro ? 'lucrări' : 'works'}
              </span>
            </div>
          </Reveal>

          {artist.works.length > 0 ? (
            <div className="grid grid-cols-12 gap-x-6 gap-y-12 border-t border-charcoal pt-8 md:pt-10">
              {artist.works.map((work, workIndex) => (
                <motion.figure
                  key={`${work.src}-${work.title}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: (workIndex % 3) * 0.05 }}
                  className="col-span-12 sm:col-span-6 lg:col-span-4"
                >
                  <div className="relative w-full h-[260px] md:h-[320px] overflow-hidden bg-beige">
                    <img
                      src={work.src}
                      alt={`${artist.name} — ${work.title}`}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-contain transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] hover:scale-[1.025]"
                    />
                  </div>
                  <figcaption className="mt-4 border-t border-charcoal pt-4">
                    <h3 className="font-sans font-extrabold text-xl md:text-2xl leading-tight text-charcoal">
                      {work.title}
                    </h3>
                    {(work.details || work.year) && (
                      <p className="mt-2 text-[11px] tracking-eyebrow uppercase text-charcoal/55 leading-relaxed">
                        {[work.details, work.year].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          ) : (
            <Reveal>
              <div className="border-t border-charcoal py-8 md:py-10 text-charcoal/55 text-base md:text-lg font-bold">
                {ro
                  ? 'Lucrările și fotografiile vor fi adăugate manual după confirmarea materialelor.'
                  : 'Works and images will be added manually after the materials are confirmed.'}
              </div>
            </Reveal>
          )}
        </section>

        <section className="border-t border-charcoal">
          <div className="grid grid-cols-2">
            <ArtistNeighbour artist={previous} direction="prev" ro={ro} />
            <ArtistNeighbour artist={next} direction="next" ro={ro} />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

const ArtistNeighbour = ({ artist, direction, ro }) => {
  const isNext = direction === 'next';

  if (!artist) {
    return (
      <div
        className={`px-6 md:px-12 lg:px-16 py-10 md:py-14 ${isNext ? 'text-right border-l border-charcoal' : ''
          } text-charcoal/35`}
      >
        <span className="text-[11px] tracking-eyebrow uppercase">
          {isNext
            ? ro ? 'Finalul listei' : 'End of list'
            : ro ? 'Începutul listei' : 'Beginning of list'}
        </span>
      </div>
    );
  }

  return (
    <Link
      to={`/artists/${artist.id}`}
      className={`group px-6 md:px-12 lg:px-16 py-10 md:py-14 ${isNext ? 'text-right border-l border-charcoal' : ''
        } block hover:bg-beige/40 transition-colors duration-500`}
    >
      <span className="text-[11px] tracking-eyebrow uppercase text-charcoal/55">
        {isNext ? (ro ? 'Următorul artist →' : 'Next artist →') : (ro ? '← Artist anterior' : '← Previous artist')}
      </span>
      <div className="mt-3 font-sans font-extrabold text-3xl md:text-4xl lg:text-5xl leading-tight text-charcoal group-hover:opacity-70 transition-opacity duration-500">
        {artist.name}
      </div>
    </Link>
  );
};

export default ArtistDetailPage;
